import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { UserUseCases, USER_USE_CASES } from '@app/domain/use-cases/user/user.use-cases.contract';
import {
  UserUpdatableProp,
  UserUpdatablePropsFactory,
  UserUpdatableTypes
} from '@app/entities/models/user/user-updatable.model';
import { User } from '@app/entities/models/user/user.model';
import { FileFetchHelper } from '@app/shared/helpers/file-fetch/file-fetch.helper';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';

import { ApiUrls, Logger } from '..';

@Injectable({ providedIn: 'root' })
export class UserSyncPropsService {
  private readonly logger: Logger = new Logger('UserSyncPropsService');

  #userUseCases: UserUseCases = inject(USER_USE_CASES);

  constructor(
    private apiUrls: ApiUrls,
    private http: HttpClient
  ) {}

  syncAllProperties() {
    const promises = Object.keys(UserUpdatableTypes)
      .filter((key: string) => Number.isNaN(Number(key)))
      .map((key: string) =>
        this.syncProperty(UserUpdatableTypes[key as keyof typeof UserUpdatableTypes] as UserUpdatableTypes)
      );
    return Promise.all(promises);
  }

  async syncProperty(dbProperty: UserUpdatableTypes): Promise<MSafeAny> {
    try {
      let user: User = await this.#userUseCases.getStoredUser();
      const updatableProperty: UserUpdatableProp = UserUpdatablePropsFactory.getUserUpdatableProp(
        dbProperty,
        this.http,
        this.apiUrls
      ) as UserUpdatableProp;

      await updatableProperty.initialize();

      if (updatableProperty.hasChanges(user)) {
        user = updatableProperty.getUserUpdated(user);

        if (dbProperty === UserUpdatableTypes.LANGUAGE) {
          await this.#userUseCases.storeLanguage(user.language_code, user.language_country);
        } else if (dbProperty === UserUpdatableTypes.IMAGE) {
          try {
            user.photo = await FileFetchHelper.getB64FromUrl(user.photo);
          } catch (err) {
            this.logger.error(err);
            user.photo = '';
          }
        }
        await this.#userUseCases.storeAvatar(user.photo);
      }
    } catch (err) {
      this.logger.error(err);
      return Promise.reject(err);
    }
  }
}
